/**
 * SignalR client for the SicBo multiplayer hub (path: /sekbaohub).
 * Port of
 *   packages/amgsicbo_v2/lib/data/repositories/sicbo_multiplayer_service.dart
 */
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { debugLog, warn } from '../util/logger';

export interface HubEvent {
  name: string;
  payload: unknown;
}

export type HubEventListener = (event: HubEvent) => void;

export const HUB_EVENTS = [
  'PlayerJoined',
  'PlayerLeft',
  'StartGame',
  'StartBet',
  'EndBet',
  'StartPlay',
  'Result',
  'PlaceBet',
  'GetMessages',
] as const;
export type HubEventName = (typeof HUB_EVENTS)[number] | '_connection';

export class MultiplayerHub {
  private readonly hubUrl: string;
  private connection: HubConnection | null = null;
  private connected = false;
  private listeners: Set<HubEventListener> = new Set();

  private lastSessionId: string | null = null;
  private lastGameKey: string | null = null;

  constructor(hubUrl: string) {
    this.hubUrl = hubUrl;
  }

  isConnected(): boolean {
    return this.connection != null && this.connected;
  }

  on(listener: HubEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async connect(): Promise<void> {
    if (this.connection) return;

    debugLog('[Hub] connecting to', this.hubUrl);
    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, { withCredentials: false })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.onclose((err) => {
      this.connected = false;
      this.emit('_connection', { state: 'closed', error: errMsg(err) });
    });
    this.connection.onreconnecting((err) => {
      this.connected = false;
      this.emit('_connection', { state: 'reconnecting', error: errMsg(err) });
    });
    this.connection.onreconnected((connectionId) => {
      this.connected = true;
      this.emit('_connection', {
        state: 'reconnected',
        connectionId: connectionId ?? null,
      });
      void this.resumeAfterReconnect();
    });

    for (const name of HUB_EVENTS) {
      this.connection.on(name, (...args: unknown[]) => {
        const payload = normalizePayload(args);
        debugLog('[Hub] event', name, payload);
        this.emit(name, payload);
      });
    }

    try {
      await this.connection.start();
      this.connected = true;
      this.emit('_connection', { state: 'connected' });
    } catch (e) {
      this.connected = false;
      warn('[Hub] start failed:', e);
      throw e;
    }
  }

  async disconnect(): Promise<void> {
    const c = this.connection;
    this.connection = null;
    this.connected = false;
    if (!c) return;
    try {
      await c.stop();
    } catch (e) {
      warn('[Hub] stop failed (ignored):', e);
    }
  }

  async joinGame(sessionId: string, gameKey: string): Promise<void> {
    this.lastSessionId = sessionId;
    this.lastGameKey = gameKey;
    await this.invoke('JoinGame', sessionId, gameKey);
  }

  async getMessages(): Promise<void> {
    await this.invoke('GetMessages');
  }

  async placeBet(betItemId: number, amount: number): Promise<void> {
    await this.invoke('PlaceBet', betItemId, amount);
  }

  private async invoke(name: string, ...args: unknown[]): Promise<void> {
    const c = this.connection;
    if (!c || c.state !== HubConnectionState.Connected) {
      warn(`[Hub] cannot invoke '${name}' — not connected (state=${c?.state})`);
      return;
    }
    debugLog('[Hub] invoke', name, args);
    try {
      await c.invoke(name, ...args);
    } catch (e) {
      warn(`[Hub] invoke '${name}' failed:`, e);
    }
  }

  private async resumeAfterReconnect(): Promise<void> {
    if (!this.lastSessionId || !this.lastGameKey) return;
    try {
      await this.joinGame(this.lastSessionId, this.lastGameKey);
      await this.getMessages();
    } catch (e) {
      warn('[Hub] resume after reconnect failed:', e);
    }
  }

  private emit(name: string, payload: unknown): void {
    const event: HubEvent = { name, payload };
    for (const l of this.listeners) {
      try {
        l(event);
      } catch (e) {
        warn('[Hub] listener threw:', e);
      }
    }
  }
}

function normalizePayload(args: unknown[]): unknown {
  if (!args || args.length === 0) return null;
  if (args.length === 1) {
    return parseIfJsonString(args[0]);
  }
  return args.map(parseIfJsonString);
}

function parseIfJsonString(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const s = v.trim();
  if (s.length === 0) return v;
  const ch = s[0];
  if (ch !== '{' && ch !== '[' && ch !== '"') return v;
  try {
    return JSON.parse(s);
  } catch {
    return v;
  }
}

function errMsg(e: unknown): string | null {
  if (e == null) return null;
  if (e instanceof Error) return e.message;
  return String(e);
}
